import { useReducer, useEffect, useCallback } from 'react'
import api, { TicketSearchResult } from '@api'
import { debounce } from 'lodash'
import { Toaster } from '@ui'
import { ApolloError } from 'apollo-boost'

enum Actions {
  SEARCH,
  LOAD,
}
interface State {
  reqId: number
  query: string
  items: TicketSearchResult[]
  loading: boolean
}

type Action<T, P> = {
  type: T
  payload: P
}
type SearchAction = Action<Actions.SEARCH, { reqId: number; query: string }>
type LoadAction = Action<Actions.LOAD, { reqId: number; tickets: TicketSearchResult[] }>
type RootAction = SearchAction | LoadAction

function ticketsReducer(state: State, action: RootAction) {
  switch (action.type) {
    case Actions.SEARCH:
      return {
        ...state,
        reqId: action.payload.reqId,
        loading: true,
        query: action.payload.query,
      }

    case Actions.LOAD:
      return state.reqId > action.payload.reqId
        ? state
        : { ...state, loading: false, items: action.payload.tickets }

    default:
      return state
  }
}

export default function useTicketSearch() {
  const [state, dispatch] = useReducer(ticketsReducer, {
    reqId: 0,
    query: '',
    items: [],
    loading: false,
  })

  const debouncedSearch = useCallback(
    debounce((query: string, reqId: number) => {
      api.ticket
        .search(query)
        .then((tickets: TicketSearchResult[]) => {
          dispatch({ type: Actions.LOAD, payload: { reqId, tickets } })
        })
        .catch((error: ApolloError) => Toaster.fail({ error }))
    }, 300),
    []
  )

  useEffect(() => {
    const reqId = Date.now()

    if (!state.query) {
      return dispatch({
        type: Actions.LOAD,
        payload: { reqId, tickets: [] },
      })
    }

    debouncedSearch(state.query, reqId)

    return () => debouncedSearch.cancel()
  }, [state.query, debouncedSearch])

  return {
    ...state,
    reload: () => debouncedSearch(state.query, Date.now()),
    run: (query: string) =>
      dispatch({
        type: Actions.SEARCH,
        payload: { query, reqId: Date.now() },
      }),
  }
}
